import { useMutation, useQuery } from "@apollo/client";
import { FileText, Upload, X } from "lucide-react";
import { useId, useState } from "react";

import { MUTATION_UPLOAD_DOCUMENT } from "api/mutations/cases";
import { QUERY_CASE_DOCUMENTS } from "api/queries/cases";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import ConfirmDialog from "pages/CaseDetail/components/ConfirmDialog";
import {
  PendingAction,
  UploadAction,
} from "pages/CaseDetail/components/Tasks/types";
import useLastDefined from "pages/CaseDetail/hooks/useLastDefined";
import useRecheck from "pages/CaseDetail/hooks/useRecheck";
import { CaseTask } from "pages/CaseDetail/types";
import { ALLOWED_FILE_TYPE_EXTENSIONS } from "pages/CaseDocuments/consts";
import { CaseDocument } from "pages/CaseDocuments/types";
import { classNames, renderLongDate } from "utils/helpers";

// The backend's API_UPLOAD_MAX_FILE_UPLOAD_SIZE_MB.
const MAX_FILE_SIZE_MB = 25;

const formatFileSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))}KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)}MB`;

interface UploadTaskDialogProps {
  caseId: string;
  pending?: PendingAction<UploadAction>;
  isStillOpen: (task: CaseTask) => Promise<boolean>;
  onClose: () => void;
  onCompleted: () => void;
}

const UploadTaskDialog = ({
  caseId,
  pending,
  isStillOpen,
  onClose,
  onCompleted,
}: UploadTaskDialogProps) => {
  const inputId = useId();
  const shown = useLastDefined(pending);
  const [file, setFile] = useState<File>();
  const {
    checking,
    problem,
    isStale,
    recheck,
    reset: resetRecheck,
  } = useRecheck();

  const { data } = useQuery<{
    data: { case: { documents: CaseDocument[] } };
  }>(QUERY_CASE_DOCUMENTS, {
    variables: { caseId },
    skip: !pending,
    fetchPolicy: "no-cache",
  });
  const lastData = useLastDefined(data);
  const uploadedDocuments =
    (pending ? data : lastData)?.data.case.documents ?? [];

  // The backend completes the case's open upload task for the document's kind.
  const [uploadDocument, { loading, error, reset }] = useMutation(
    MUTATION_UPLOAD_DOCUMENT,
    { onCompleted: () => onCompleted() },
  );

  if (!shown) return null;

  const isTooLarge = (file?.size ?? 0) > MAX_FILE_SIZE_MB * 1024 * 1024;

  return (
    <ConfirmDialog
      open={!!pending}
      onClose={onClose}
      afterLeave={() => {
        setFile(undefined);
        reset();
        resetRecheck();
      }}
      onConfirm={async () => {
        if (
          !(await recheck(
            () => isStillOpen(shown.task),
            "This task has already been completed.",
          ))
        ) {
          return;
        }
        uploadDocument({
          variables: {
            caseUuid: caseId,
            kind: shown.action.documentKind,
            file,
            applicationUuid: shown.action.applicationUuid,
          },
        }).catch(() => {});
      }}
      loading={checking || loading}
      error={problem || (error ? error.message || true : false)}
      title={shown.task.title}
      confirmLabel="Upload"
      confirmDisabled={!file || isTooLarge || isStale}
    >
      <div className="space-y-5">
        {uploadedDocuments.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-brand-copy-primary">
              Already uploaded to this case
            </p>
            <ul className="divide-y rounded-md border text-sm">
              {uploadedDocuments.map((document) => (
                <li
                  key={document.uuid}
                  className="flex items-center gap-3 px-3 py-2"
                >
                  <FileText
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-brand-copy-primary">
                      {document.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {document.kindLabel} |{" "}
                      {renderLongDate(document.createdOn)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor={inputId}>{shown.action.label}</Label>
          <input
            id={inputId}
            type="file"
            accept={ALLOWED_FILE_TYPE_EXTENSIONS}
            className="peer sr-only"
            onChange={(event) => {
              setFile(event.target.files?.[0]);
              // Lets the same file be picked again after removing it.
              event.target.value = "";
            }}
          />
          {file ? (
            <div
              className={classNames(
                "flex items-center gap-3 rounded-md border px-3 py-2 text-sm",
                isTooLarge
                  ? "border-danger-border bg-danger-soft"
                  : "bg-muted/50",
              )}
            >
              <FileText
                className="size-4 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-brand-copy-primary">{file.name}</p>
                <p
                  className={classNames(
                    "text-xs",
                    isTooLarge ? "text-danger" : "text-muted-foreground",
                  )}
                >
                  {isTooLarge
                    ? `This file is over ${MAX_FILE_SIZE_MB}MB.`
                    : formatFileSize(file.size)}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 shrink-0"
                aria-label={`Remove ${file.name}`}
                onClick={() => setFile(undefined)}
              >
                <X className="size-4" />
              </Button>
            </div>
          ) : (
            <label
              htmlFor={inputId}
              className="flex cursor-pointer flex-col items-center gap-1 rounded-md border border-dashed px-4 py-6 text-center text-sm transition-colors hover:bg-muted/50 peer-focus-visible:ring-2 peer-focus-visible:ring-ring"
            >
              <Upload
                className="mb-1 size-5 text-muted-foreground"
                aria-hidden="true"
              />
              <span className="font-medium text-brand-copy-primary">
                Choose a file
              </span>
              <span className="text-xs text-muted-foreground">
                PDF, image or Word, up to {MAX_FILE_SIZE_MB}MB
              </span>
            </label>
          )}
        </div>
      </div>
    </ConfirmDialog>
  );
};

export default UploadTaskDialog;
